<template>
  <LoadingSpinner v-if="isPending" />

  <div v-else class="show-purchase-order no-print">
    <AppPageHeader :title="`PO# ${purchaseOrder.purchaseOrderId }`">
      <template #actions>
        <BaseDropdown
          title="Manage"
          right
          :items="[
            { title: 'Edit Purchase Order', to: { name: 'EditPurchaseOrder', params: { id: purchaseOrder.id } } },
            { title: 'Email to Vendor', action: generatePurchaseOrderPdf },
            { title: 'Print', action: print },
            { title: 'Delete', action: () => showDeleteConfirmationModal = true }
          ]"
        />
      </template>
    </AppPageHeader>

    <!-- Purchase order detail -->
    <div class="grid grid-cols-6 gap-x-2 border-b border-slate-300 pb-4 mb-4">
      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Status</h3>
        <BaseBadge
          :variant="purchaseOrder.status === PURCHASE_ORDER_STATUS.CLOSED ? 'success' : 'warning'"
        >
          <span class="capitalize">{{ purchaseOrder.status }}</span>
        </BaseBadge>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Date</h3>
        <span class="text-sm font-weight-bold">{{ formatDate(purchaseOrder.date) }}</span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">PO #</h3>
        <span class="text-sm font-weight-bold">{{ purchaseOrder.purchaseOrderId }}</span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Project</h3>
        <span class="text-sm font-weight-bold">
          <router-link v-if="project?.id" :to="{ name: 'ShowProject', params: { projectId: project?.id, customerId: project?.customerId } }">
            {{ project?.name }}
          </router-link>
          <span v-else>{{ purchaseOrder.projectId }}</span>
        </span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Vendor</h3>
        <span class="text-sm font-weight-bold">
          <router-link v-if="vendor?.id" :to="{ name: 'ShowVendor', params: { id: vendor?.id } }">
            {{ vendor.name }}
          </router-link>
          <span v-else>{{ purchaseOrder.vendorId }}</span>
        </span>
      </div>

      <div>
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Total</h3>
        <span class="text-sm font-weight-bold">{{ currency(purchaseOrder.total) }}</span>
      </div>
    </div>

    <BaseTable
      :items="purchaseOrder.items"
      :fields="purchaseOrderItemFields"
      class="mt-5"
    >
      <template #cost-data="{ row }">
        {{ currency(row.cost) }}
      </template>
      <template #total-data="{ row }">
        {{ currency(row.quantity * row.cost) }}
      </template>
      <template #description-data="{ row }">
        <span class="!text-wrap">{{ row.description }}</span>
      </template>
    </BaseTable>

    <hr class="border-b-slate-200 border-2" />

    <!-- totals -->
    <div class="mt-5 flex flex-col bg-slate-100 w-1/3 p-4 rounded-lg sm:ml-auto">
      <div class="flex justify-between">
        <span class="text-sm font-medium text-slate-500">Total</span>
        <span class="text-sm font-medium text-slate-500">{{ currency(purchaseOrder.total) }}</span>
      </div>
    </div>

    <!-- <div class="confirmation">
      <SuccessModal :visible="showConfirmationModal">
        <template #body>
          <p>Your purchase order has been submited successfully. You can send it to the vendor with the PO number <b>{{ purchaseOrder.purchaseOrderId }}</b></p>
        </template>
        <template #confirm-button>
          <BaseButton
            block
            variant="primary"
            :to="{ hash: '#show-email' }"
          >
            Email
          </BaseButton>
        </template>
      </SuccessModal>
    </div> -->

    <DeleteConfirmationModal
      id="deleteConfirmationModal"
      v-model="showDeleteConfirmationModal"
      title="Delete Purchase Order"
      :is-deleting="deletingPO"
      @delete="deletePurchaseOrder"
    >
      <div class="mt-2">
        <p class="text-base mb-3">Are you sure you want to delete this purchase order?</p>
        <p class="text-sm mb-2">This will also delete:</p>
        <ul class="text-sm list-disc ml-6">
          <li>All Bills (if they exist)</li>
        </ul>
      </div>
    </DeleteConfirmationModal>

    <Modal
      id="email-purchase-order"
      v-model="showEmailModal"
      title="Send Purchase Order"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
      width="w-full md:w-[80%] lg:max-w-5xl xl:max-w-6xl"
    >
      <EmailPurchaseOrder
        :purchase-order="purchaseOrder"
        :pdf-link="pdfLink"
        :pdf-ready="pdfReady"
        :email="(email as Email)"
        @close="showEmailModal = false"
      />
    </Modal>
  </div>

  <div
    v-if="!isLoading && purchaseOrder.id"
    class="no-print"
  >
    <div
      v-show="!pdfReady"
      ref="purchaseOrderToPdf"
    >
      <PurchaseOrderToPdf
        :vendor="vendor"
        :purchase-order="purchaseOrder"
      />
    </div>
  </div>

  <div
    v-if="!isLoading && purchaseOrder.id"
    id="printContent"
    class="hide-print-content"
  >
    <PrintPurchaseOrder
      :vendor="vendor"
      :purchase-order="purchaseOrder"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
// import SuccessModal from '@/components/SucessModal.vue'
import EmailPurchaseOrder from '../components/EmailPurchaseOrder.vue'
import PurchaseOrderToPdf from '../components/PurchaseOrderToPdf.vue'
import html2pdf from 'html2pdf.js'
import DeleteConfirmationModal from '@/components/app/DeleteConfirmationModal.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { currency, formatDate } from '@/utils'
import { useNotification } from '@kyvg/vue3-notification'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import PurchaseOrderService from '@/services/PurchaseOrderService'
import { PURCHASE_ORDER_STATUS } from '@/models/purchaseOrder'
import useGetAllvendors from '@/composables/useGetAllVendors'
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import ProjectService from '@/services/ProjectService'
import PrintPurchaseOrder from '../components/PrintPurchaseOrder.vue'
import Modal from '@/components/app/AppModal.vue'
import { Email } from '@/models/email'
import EmailService, { EmailTemplate } from '@/services/EmailService'
import Handlebars from 'handlebars'

const $route = useRoute()
const $router = useRouter()
const queryClient = useQueryClient()
const { notify } = useNotification()
const purchaseOrderId = $route.params.id as string
const purchaseOrderItemFields = [
  { key: 'name', label: 'Items' },
  { key: 'description', label: 'Description' },
  { key: 'cost', label: 'Price' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'total', label: 'Total' }
]

const showConfirmationModal = ref(false)
const showDeleteConfirmationModal = ref(false)
const showEmailModal = ref(false)
const pdfReady = ref(true)
const pdfLink = ref('')
const purchaseOrderToPdf = useTemplateRef('purchaseOrderToPdf')

const purchaseOrderService = new PurchaseOrderService('')
const { data: purchaseOrder, isPending } = useQuery({
  queryKey: ['purchaseOrder', purchaseOrderId],
  queryFn: async () => {
    return await purchaseOrderService.find(purchaseOrderId)
  }
})

const { data: vendors } = useGetAllvendors()
const vendor = computed(() => {
  return vendors.value?.find((vendor) => vendor.id === purchaseOrder.value.vendorId)
})

const { data: project } = useQuery({
  queryKey: ['project', purchaseOrder.value?.projectId],
  queryFn: async () => {
    const projectService = new ProjectService()
    return await projectService.find(purchaseOrder.value?.projectId)
  },
  enabled: !!purchaseOrder.value?.projectId
})

onMounted(() => {
  showConfirmationModal.value = $route.hash === '#show-confirmation'
  showEmailModal.value = $route.hash === '#show-email'
})

const print = () => {
  window.print()
}

const generatePurchaseOrderPdf = async () => {
  emailPurchaseOrder()
  showEmailModal.value = true
  pdfReady.value = false

  const opt = {
    margin: 0.5,
    filename: 'PurchaseOrder.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
  }

  const attachment = await html2pdf().from(purchaseOrderToPdf.value).set(opt).toPdf().get('pdf').then((pdf) => {
    const blob = pdf.output('blob')
    pdfLink.value = URL.createObjectURL(blob)
    pdfReady.value = true
    return blob
  })

  const reader = new FileReader()
  reader.readAsDataURL(attachment)
  reader.onloadend = () => {
    const base64data = (reader.result as string).split(',')[1]
    email.value.message.attachments = [{
      filename: 'purchaseOrder.pdf',
      content: base64data,
      encoding: 'base64'
    }]
    pdfReady.value = true
  }
}

const { mutateAsync: deletePurchaseOrder, isPending: deletingPO } = useMutation({
  mutationFn: () =>  purchaseOrderService.deletePOWithBills(purchaseOrder.value),
  onSuccess: () => {
    queryClient.removeQueries({ queryKey: ['purchaseOrder', purchaseOrderId] })
    queryClient.removeQueries({ queryKey: ['purchaseOrder', purchaseOrderId] })
    queryClient.removeQueries({ queryKey: ['PurchaseOrders', purchaseOrder.value?.projectId], exact: false })
    queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] })
    queryClient.invalidateQueries({ queryKey: ['firestore', 'bills'], exact: false })

    notify({
      type: 'success',
      title: 'Purchase order deleted',
      text: 'The purchase order has been deleted successfully'
    })
    $router.push({ name: 'ListPurchaseOrders' })
  },
  onError: (error) => {
    console.error(error)
    notify({
      type: 'error',
      title: 'Failed to delete purchase order',
      text: error.message
    })
  }
})


const emailPurchaseOrder = () => {
  const link = `${window.location.origin}/purchaseOrder/${purchaseOrder.value.id}`
  const emailData = { vendorName: vendor.value.name, link: link, purchaseOrderId: purchaseOrder.value.purchaseOrderId }

  email.value.to = [vendor.value.email]
  email.value.from = 'portal-admin@uttrly.com'

  email.value.message.subject = Handlebars.compile(emailTemplate.value.subject).call(this, { purchaseOrderId: purchaseOrder.value.purchaseOrderId })

  Handlebars.registerPartial(baseTempalte.value.name, baseTempalte.value.html)
  email.value.message.html = Handlebars.compile(emailTemplate.value.html).call(this, emailData)
  showEmailModal.value = true
}

const email = ref({...new Email(), to: [], cc: []})
const emailService = new EmailService()

const { data: baseTempalte } = useQuery({
  queryKey: ['emailTemplates/base_layout'],
  queryFn: async () => {
    return await emailService.find('base_layout') || new EmailTemplate()
  }
})

const { data: emailTemplate } = useQuery({
  queryKey: ['emailTemplates/send_purchase_order'],
  queryFn: async () => {
    return await emailService.find('send_purchase_order') || new EmailTemplate()
  }
})

const isLoading = computed(() => isPending.value || !vendor.value?.id)
</script>


<style lang="scss" scoped>
@media print {
  .no-print {
    display: none;
  }
  #printContent {
    display: block;
  }
}

.hide-print-content {
  display: none;
}
</style>
